import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingSlotLabelsBulkImportModal } from "./ManufacturingSlotLabelsBulkImportModal";
import { ManufacturingSlotLabelsRenamePanel } from "./ManufacturingSlotLabelsRenamePanel";
import { ManufacturingSlotLabelsUnlabeledPanel } from "./ManufacturingSlotLabelsUnlabeledPanel";

export type SlotLabelsMaterialsFocus = {
  scope: "all";
  search: string;
  nonce: number;
};

export function ManufacturingSlotLabelsPanel({
  enabled,
  refreshTick,
  orgSlotLabels = [],
  onLabelsChanged,
}: {
  enabled: boolean;
  refreshTick: number;
  orgSlotLabels?: readonly string[];
  onLabelsChanged?: () => void;
}) {
  const [materialsFocus, setMaterialsFocus] = useState<SlotLabelsMaterialsFocus | null>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);

  function handleLabelsChanged() {
    setLocalRefresh((n) => n + 1);
    onLabelsChanged?.();
  }

  const effectiveRefreshTick = refreshTick + localRefresh;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => setShowBulkImport(true)}
          disabled={!enabled}
        >
          Bulk import
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard
          title="Material slot labels"
          description="Browse unlabeled or labeled BOM materials, filter by recipe, and apply or correct slot labels."
        >
          <ManufacturingSlotLabelsUnlabeledPanel
            enabled={enabled}
            refreshTick={effectiveRefreshTick}
            focusRequest={materialsFocus}
            orgSlotLabels={orgSlotLabels}
            onApplied={handleLabelsChanged}
          />
        </SectionCard>
        <SectionCard
          title="Slot label inventory"
          description="Search labels, fix inconsistencies like band_board vs band_boards, and rename across recipes."
        >
          <ManufacturingSlotLabelsRenamePanel
            enabled={enabled}
            refreshTick={effectiveRefreshTick}
            onRenamed={handleLabelsChanged}
            onShowAssociatedRecipes={(label) =>
              setMaterialsFocus({ scope: "all", search: label, nonce: Date.now() })
            }
          />
        </SectionCard>
      </div>

      {showBulkImport ? (
        <ManufacturingSlotLabelsBulkImportModal
          enabled={enabled}
          onClose={() => setShowBulkImport(false)}
          onImported={handleLabelsChanged}
        />
      ) : null}
    </div>
  );
}
